import { HttpBackend, HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastController } from '@ionic/angular';
import { Observable, throwError } from 'rxjs';
import { catchError, map, share, tap } from 'rxjs/operators';
import { AppConfig } from '../helpers/app-config';
import { Company, User } from '../models/user';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private companies: Company[] = [];
  private ht: HttpClient;

  constructor(
    private router: Router,
    private config: AppConfig,
    private handler: HttpBackend,
    private http: HttpClient,
    private toastController: ToastController
  ) {
    this.ht = new HttpClient(this.handler); // bypass interceptors
  }

  getCompanies(username: string): Observable<User> {
    const params = new HttpParams().set('username', username);
    return this.ht.get<User>(`${this.config.api}/client/mobilequery/`, { params });
  }

  login(user: User): Observable<any> {
    return this.http.post(`${this.config.api}-auth/token/obtain/`, user).pipe(
      tap((response: any) => {
        if (response?.access) {
          this.setSession(response);
        }
      }),
    //  catchError(this.handleError)
    );
  }

  // auth.service.ts
  loginTr069(user: any): Observable<any> {
    return this.http.post(`${this.config.tr069UrlApi2}/token/client`, user).pipe(
      tap((response: any) => {
        if (response?.accessToken) {
          // Optionally store TR069 token separately if needed
          localStorage.setItem('tr069_access_token', response.accessToken);
          localStorage.setItem('tr069_refresh_token', response.refreshToken);
        }
      }),
      catchError(this.handleError)
    );
  }


  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshtoken');
    localStorage.removeItem('user');
    localStorage.removeItem('username');
    localStorage.removeItem('first_name');
    localStorage.removeItem('last_name');
    localStorage.removeItem('company');
    localStorage.removeItem('security_token');
    localStorage.removeItem('password_reset');
    localStorage.removeItem('company_name');

    this.router.navigate(['/auth']);
  }

  setCompanies(companies: Company[]): void {
    this.companies = companies;
  }

  getCompaniesArr(): Company[] {
    return this.companies;
  }

  async presentToast(position: 'top' | 'middle' | 'bottom', message: string): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration: 1500,
      position
    });

    await toast.present();
  }
  refreshTr069Token(): Observable<string> {
    const refresh = localStorage.getItem('tr069_refresh_token');

    if (!refresh) {
      return throwError(() => new Error('No TR-069 refresh token available.'));
    }

    return this.http.post(this.config.tr069UrlApi2 + '/token/refresh-token', {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      refresh_token: refresh
    }).pipe(
      map((response: any) => {
        if (response?.tr069_access_token && response?.tr069_refresh_token) {
          localStorage.setItem('tr069_access_token', response.tr069_access_token);
          localStorage.setItem('tr069_refresh_token', response.tr069_refresh_token);
          return response.tr069_access_token;
        }
        throw new Error('Unable to refresh TR-069 token.');
      }),
      catchError(error => {
        this.logout(); // Log out user on TR-069 token refresh failure
        return throwError(() => error);
      })
    );
  }


  setSession(response: any): void {
    localStorage.setItem('token', response.access);
    localStorage.setItem('refreshtoken', response.refresh);

    const user = this.parseToken(response.access);

    // console.log('user data received', user);

    localStorage.setItem('user', user.user || '');
    localStorage.setItem('security_token', user.security_token || '');
    localStorage.setItem('username', user.username || '');
    localStorage.setItem('first_name', user.first_name || '');
    localStorage.setItem('last_name', user.last_name || '');
    localStorage.setItem('company', user.company_id || '');
    localStorage.setItem('password_reset', user.password_reset || '');
    localStorage.setItem('company_name', user.company_name || '');
    localStorage.setItem('sn', user.cpe_serial_number || '');
  }

  parseToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Failed to parse token', e);
      return {};
    }
  }

  getUserDetails(): any {
    const token = localStorage.getItem('token');
    return token ? this.parseToken(token) : null;
  }

  recoverAccount(user: any): Observable<any> {
    return this.http.post(`${this.config.api}/client/accountrecovery/`, user);
  }

  selectedCompany(company: Company): void {
    console.log(company);
    localStorage.setItem('company', company.company);
  }

  getSelectedCompany(): string | null {
    return localStorage.getItem('company');
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  getUsername(): string | null {
    return localStorage.getItem('username');
  }

  refreshToken(): Observable<string | null> {
    const refresh = this.getRefreshToken();

    return this.http.post(`${this.config.api}-auth/token/refresh/`, { refresh }).pipe(
      share(),
      map((response: any) => {
        if (response?.access) {
          this.setSession(response);
          return response.access;
        }
        return null;
      }),
      catchError(this.handleRefreshError)
    );
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refreshtoken'); // fixed key name to match what's being set
  }

  private handleError(error: HttpErrorResponse | any): Observable<never> {
    console.error('An error occurred:', error,error.message);
    return throwError(error);
  }

  private handleRefreshError(error: HttpErrorResponse | any): Observable<never> {
    console.error('A refresh error occurred:', error);
    return throwError(() => error.message || 'Token Refresh Error');
  }
}
